import React, { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { addPendingPhoto, listPendingPhotos, removePendingPhoto } from "../../lib/offline";
import { Camera, Mic, X, ChevronDown, ChevronUp, Trash2, Loader2, CloudOff, Image as ImageIcon } from "lucide-react";

export const GROWTH_STATUS = ["Very Slow", "Slow", "Normal", "Fast", "Very Fast"];
export const VIGOR_LEVELS = ["Very Low", "Low", "Moderate", "High", "Very High"];
export const UNIFORMITY = ["Uniform", "Variable"];
export const VIGOR_OBS = ["Weak vines", "Excessive vigor", "Uneven growth", "Short internodes", "Long internodes",
  "Dense canopy", "Open canopy", "Missing vines", "Chlorosis", "Water stress", "Other"];
export const GWI = ["Good", "Watch", "Issue"];
export const SEVERITIES = ["Trace", "Low", "Moderate", "High", "Severe"];
export const HEALTH_ISSUES = ["Nutrient symptoms", "Chlorosis", "Water stress", "Frost damage", "Heat stress",
  "Sunburn", "Wind damage", "Mechanical damage", "Herbicide injury", "Chemical injury",
  "Trunk disease symptoms", "Dead vines", "Missing vines", "Other"];
export const DISEASES = ["Powdery mildew", "Botrytis", "Phomopsis", "Downy mildew", "Sour rot", "Trunk disease", "Crown gall"];
export const INSECTS = ["Omnivorous leafroller", "Vine mealybug", "Grape mealybug", "Leafhopper",
  "Western grapeleaf skeletonizer", "Spider mites", "Thrips", "Sharpshooters", "Other insect"];
export const WILDLIFE = ["Gophers", "Voles", "Deer", "Birds", "Wild pigs", "Other wildlife"];
export const IRRIGATION_ISSUES = ["Dry", "Too wet", "Water stress", "Leak", "Broken line", "Clogged emitter",
  "Broken emitter", "Pressure issue", "Filter issue", "Valve issue", "Pump issue", "Mainline issue", "Uneven irrigation", "Other"];
export const WEED_PRESSURE = ["None", "Low", "Moderate", "High", "Severe"];
export const FLOOR_ITEMS = ["Cover crop OK", "Mowing needed", "Cultivation needed", "Under-vine work needed",
  "Road work needed", "Erosion issue", "Other"];
export const CULTURAL_OPS = ["Pruning", "Tying", "Suckering", "Shoot thinning", "Shoot positioning", "First wire lift",
  "Second wire lift", "Additional wire lifting", "Leaf removal", "Hedging", "Lateral removal", "Crop thinning",
  "Cluster thinning", "Bird netting", "Harvest preparation", "Post-harvest work", "Other"];
export const CULTURAL_STATUS = ["NOT NEEDED", "UPCOMING", "READY", "IN PROGRESS", "COMPLETE", "NEEDS CORRECTION"];
export const PHENOLOGY_STAGES = ["Dormant", "Bud Swell", "Budbreak", '1-3" Shoots', '4-8" Shoots', '10-16" Shoots',
  "Pre-Bloom", "Bloom", "Fruit Set", "Berry Development", "Bunch Closure", "Veraison",
  "Post-Veraison", "Harvest Ready", "Harvested", "Post-Harvest", "Leaf Fall"];
export const TASK_CATEGORIES = ["CREW WORK", "TRACTOR WORK", "SPRAY EVALUATION", "IRRIGATION", "IRRIGATION REPAIR",
  "SCOUTING", "SAMPLING", "REPAIR", "MANAGER FOLLOW-UP", "OTHER"];

export const PRIO_STYLE = {
  urgent: "bg-red-600 text-white", high: "bg-amber-500 text-white",
  normal: "bg-[#1F3D34] text-white", low: "bg-stone-400 text-white",
};
export const SEV_STYLE = {
  Trace: "bg-stone-200 text-stone-700", Low: "bg-lime-200 text-lime-900", Moderate: "bg-amber-200 text-amber-900",
  High: "bg-orange-500 text-white", Severe: "bg-red-600 text-white",
};

export const bLabel = (s) => (s ? (/^block/i.test(String(s).trim()) ? s : `Block ${s}`) : "");

export function Chips({ options, value, onChange, multi = false, testId, small = false }) {
  const isSel = (o) => (multi ? (value || []).includes(o) : value === o);
  const toggle = (o) => {
    if (multi) {
      const cur = value || [];
      onChange(cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]);
    } else onChange(value === o ? null : o);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`${small ? "h-9 px-3 text-xs" : "h-11 px-4 text-sm"} border font-semibold transition-colors ${
            isSel(o) ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white text-stone-700 border-stone-300 hover:border-[#7C9673]"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export function Section({ title, badge, tone, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  const toneCls = tone === "issue" ? "border-l-4 border-l-red-500" : tone === "watch" ? "border-l-4 border-l-amber-500" : tone === "set" ? "border-l-4 border-l-[#7C9673]" : "";
  return (
    <div className={`bg-white border border-stone-200 ${toneCls}`} data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3.5 text-left">
        <span className="font-bold uppercase tracking-wide text-sm">{title}</span>
        <span className="flex items-center gap-2">
          {badge && <span className="text-xs font-semibold px-2 py-0.5 bg-[#7C9673]/20 text-[#1F3D34]">{badge}</span>}
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1.5 text-stone-600">{label}</label>
      {children}
    </div>
  );
}

export function AuthImg({ photoId, className, alt = "photo" }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API}/scouting/photos/${photoId}/image`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
        if (res.ok) { revoked = URL.createObjectURL(await res.blob()); setUrl(revoked); }
      } catch (e) { console.warn("AuthImg load failed", e); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [photoId]);
  if (!url) return <div className={`bg-stone-100 animate-pulse ${className}`} />;
  return <img src={url} alt={alt} className={className} />;
}

async function compressImage(file, maxDim = 1600, quality = 0.8) {
  if (!file || !file.type?.startsWith("image/") || file.size < 400 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch (e) {
    return file;
  }
}

export function PhotoUploader({ visitId, ranchId, category, photos, onChange }) {
  const cameraRef = useRef();
  const libraryRef = useRef();
  const [busy, setBusy] = useState(false);
  const [pendingList, setPendingList] = useState([]);

  const loadPending = async () => {
    const all = await listPendingPhotos();
    setPendingList(all.filter((p) => p.visit_id === visitId && p.ranch_id === ranchId));
  };
  useEffect(() => { loadPending(); const t = setInterval(loadPending, 6000); return () => clearInterval(t); }, [visitId, ranchId]); // eslint-disable-line

  const getPos = () => new Promise((res) => {
    if (!navigator.geolocation) return res(null);
    navigator.geolocation.getCurrentPosition((p) => res(p), () => res(null), { timeout: 3000 });
  });

  const upload = async (rawFile) => {
    if (!rawFile) return;
    setBusy(true);
    try {
      const file = await compressImage(rawFile);
      const pos = await getPos();
      const fd = new FormData();
      fd.append("file", file);
      fd.append("visit_id", visitId);
      if (ranchId) fd.append("ranch_id", ranchId);
      fd.append("category", category || "General");
      if (pos) { fd.append("latitude", pos.coords.latitude); fd.append("longitude", pos.coords.longitude); }
      try {
        const { data } = await api.post("/scouting/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange([...(photos || []), data]);
      } catch (e) {
        const offline = !e.response || e.response.status === 503 || !navigator.onLine;
        if (!offline) throw e;
        await addPendingPhoto({
          id: `pp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          visit_id: visitId, ranch_id: ranchId || null, category: category || "General",
          caption: "", client_eligible: false, blob: file,
          filename: file.name || "photo.jpg", content_type: file.type || "image/jpeg",
          latitude: pos?.coords?.latitude ?? null, longitude: pos?.coords?.longitude ?? null,
          queued_at: new Date().toISOString(),
        });
        await loadPending();
      }
    } finally { setBusy(false); }
  };
  const update = async (pid, upd) => {
    const { data } = await api.put(`/scouting/photos/${pid}`, upd);
    onChange(photos.map((p) => (p.id === pid ? data : p)));
  };
  const remove = async (pid) => {
    await api.delete(`/scouting/photos/${pid}`);
    onChange(photos.filter((p) => p.id !== pid));
  };
  const updatePending = async (item, upd) => {
    await addPendingPhoto({ ...item, ...upd });
    await loadPending();
  };
  const removePending = async (id) => {
    await removePendingPhoto(id);
    await loadPending();
  };
  const mine = (photos || []).filter((p) => !category || p.category === category);
  const minePending = pendingList.filter((p) => !category || p.category === category);
  const slug = (category || "general").toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="space-y-2">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid={`photo-input-${slug}`}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={libraryRef} type="file" accept="image/*" multiple className="hidden"
        data-testid={`photo-input-lib-${slug}`}
        onChange={(e) => { [...(e.target.files || [])].forEach((f) => upload(f)); e.target.value = ""; }} />
      <div className="flex gap-2">
        <button type="button" data-testid={`photo-btn-${slug}`}
          onClick={() => cameraRef.current?.click()} disabled={busy}
          className="h-11 px-4 border border-stone-300 bg-white font-semibold text-sm flex items-center gap-2 hover:border-[#7C9673] disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} {busy ? "Uploading…" : "Take Photo"}
        </button>
        <button type="button" data-testid={`photo-lib-btn-${slug}`}
          onClick={() => libraryRef.current?.click()} disabled={busy}
          className="h-11 px-4 border border-stone-300 bg-white font-semibold text-sm flex items-center gap-2 hover:border-[#7C9673] disabled:opacity-50">
          <ImageIcon size={16} /> Library
        </button>
      </div>
      {(mine.length > 0 || minePending.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {mine.map((p) => (
            <div key={p.id} className="relative border border-stone-200 bg-white">
              <AuthImg photoId={p.id} className="w-full h-24 object-cover" />
              <button type="button" onClick={() => remove(p.id)} className="absolute top-1 right-1 bg-black/60 text-white p-1"><Trash2 size={12} /></button>
              <input placeholder="Caption" defaultValue={p.caption || ""} onBlur={(e) => e.target.value !== (p.caption || "") && update(p.id, { caption: e.target.value })}
                className="w-full text-[10px] px-1 py-1 border-t border-stone-200" />
              <button type="button" onClick={() => update(p.id, { client_eligible: !p.client_eligible })}
                className={`w-full text-[9px] font-bold uppercase py-1 ${p.client_eligible ? "bg-[#7C9673] text-white" : "bg-stone-100 text-stone-500"}`}>
                {p.client_eligible ? "Client Eligible" : "Internal Only"}
              </button>
            </div>
          ))}
          {minePending.map((p) => (
            <div key={p.id} className="relative border-2 border-dashed border-amber-400 bg-white" data-testid={`pending-photo-${p.id}`}>
              <PendingThumb blob={p.blob} />
              <div className="absolute top-1 left-1 bg-amber-500 text-white text-[8px] font-bold uppercase px-1.5 py-0.5 flex items-center gap-1"><CloudOff size={9} /> Pending</div>
              <button type="button" onClick={() => removePending(p.id)} className="absolute top-1 right-1 bg-black/60 text-white p-1"><Trash2 size={12} /></button>
              <input placeholder="Caption" defaultValue={p.caption || ""} onBlur={(e) => e.target.value !== (p.caption || "") && updatePending(p, { caption: e.target.value })}
                className="w-full text-[10px] px-1 py-1 border-t border-stone-200" />
              <button type="button" onClick={() => updatePending(p, { client_eligible: !p.client_eligible })}
                className={`w-full text-[9px] font-bold uppercase py-1 ${p.client_eligible ? "bg-[#7C9673] text-white" : "bg-stone-100 text-stone-500"}`}>
                {p.client_eligible ? "Client Eligible" : "Internal Only"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PendingThumb({ blob }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url ? <img src={url} alt="pending" className="w-full h-24 object-cover opacity-80" /> : <div className="w-full h-24 bg-stone-100" />;
}

export function VoiceNote({ onDraft }) {
  const [listening, setListening] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    rec.onresult = (e) => {
      let final = "";
      for (const r of e.results) final += r[0].transcript;
      setText(final);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  };
  const stop = () => { recRef.current?.stop(); setListening(false); };
  const extract = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scouting/voice-extract", { text });
      onDraft(data.draft, text);
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-200 p-4 space-y-3" data-testid="voice-note">
      <div className="flex items-center justify-between">
        <span className="font-bold uppercase tracking-wide text-sm">Voice Note</span>
        {supported && (
          <button type="button" data-testid="voice-mic-btn" onClick={listening ? stop : start}
            className={`h-11 px-4 font-bold text-sm flex items-center gap-2 border ${listening ? "bg-red-600 text-white border-red-600 animate-pulse" : "bg-white border-stone-300"}`}>
            <Mic size={16} /> {listening ? "Stop" : "Dictate"}
          </button>
        )}
      </div>
      <textarea data-testid="voice-text" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder='e.g. "Shoots about 24 inches, growing fast. First wire next week. Weeds getting heavy on the west side, small leak near row 18."'
        className="w-full border border-stone-200 p-3 text-sm" />
      <button type="button" data-testid="voice-extract-btn" onClick={extract} disabled={busy || !text.trim()}
        className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-sm disabled:opacity-40 flex items-center gap-2">
        {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Extracting…" : "Extract Draft (Review Required)"}
      </button>
    </div>
  );
}

export function TaskModal({ open, onClose, defaults = {}, onCreated }) {
  const [meta, setMeta] = useState({ categories: TASK_CATEGORIES, priorities: ["urgent", "high", "normal", "low"] });
  const [employees, setEmployees] = useState([]);
  const [crews, setCrews] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [clients, setClients] = useState([]);
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setT({ title: "", category: "CREW WORK", priority: "normal", instructions: "", due_date: "",
      ranch_number: "", vineyard_name: "", block_ids: [], block_labels: [], acres: null,
      supervisor_id: "", crew_id: "", employee_ids: [], equipment_ids: [], ...defaults });
    (async () => {
      const [m, e, c, q, r, cl] = await Promise.all([
        api.get("/tasks/meta"), api.get("/employees"), api.get("/crews"), api.get("/equipment"),
        api.get("/ranches"), api.get("/clients")]);
      setMeta(m.data); setEmployees(e.data.filter((x) => x.active)); setCrews(c.data); setEquipment(q.data.filter((x) => x.active));
      setRanches(r.data); setClients(cl.data);
    })();
  }, [open]); // eslint-disable-line
  if (!open || !t) return null;
  const fromScouting = !!defaults.visit_id;
  const vineyards = [...new Map(ranches.map((r) => [r.ranch_number, r])).values()];
  const blocksFor = (rn) => ranches.filter((r) => r.ranch_number === rn);
  const pickVineyard = (rn) => {
    const first = ranches.find((r) => r.ranch_number === rn);
    const client = clients.find((c) => c.id === first?.client_id);
    setT({ ...t, ranch_number: rn, vineyard_name: first?.name || "", block_ids: [], block_labels: [], acres: null,
      client_id: first?.client_id || null, client_name: client?.name || null });
  };
  const toggleBlock = (b) => {
    const sel = t.block_ids.includes(b.id);
    const block_ids = sel ? t.block_ids.filter((x) => x !== b.id) : [...t.block_ids, b.id];
    const rows = ranches.filter((r) => block_ids.includes(r.id));
    setT({ ...t, block_ids, block_labels: rows.map((r) => r.block || r.name),
      acres: rows.length ? Math.round(rows.reduce((s, r) => s + (r.acres || 0), 0) * 10) / 10 : null });
  };
  const save = async () => {
    if (!t.title.trim()) return;
    setSaving(true);
    try {
      const supervisor = employees.find((e) => e.id === t.supervisor_id);
      const crew = crews.find((c) => c.id === t.crew_id);
      const body = { ...t,
        supervisor_name: supervisor?.name || null,
        crew_name: crew?.name || null,
        employee_names: employees.filter((e) => t.employee_ids.includes(e.id)).map((e) => e.name),
        equipment_names: equipment.filter((e) => t.equipment_ids.includes(e.id)).map((e) => `${e.code || ""} ${e.name}`.trim()),
      };
      const { data } = await api.post("/tasks", body);
      onCreated?.(data);
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="task-modal">
      <div className="bg-white border border-stone-200 w-full max-w-xl max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Create Task</h3>
          <button onClick={onClose} data-testid="task-modal-close"><X size={20} /></button>
        </div>
        {fromScouting && (defaults.vineyard_name || defaults.block_labels?.length > 0) && (
          <div className="bg-[#7C9673]/10 border border-[#7C9673]/40 p-3 text-xs font-semibold text-[#1F3D34]">
            {defaults.client_name && <span>{defaults.client_name} · </span>}
            {defaults.vineyard_name}{defaults.block_labels?.length ? ` · ${defaults.block_labels.map(bLabel).join(", ")}` : ""}
            {defaults.observation_note && <div className="mt-1 text-stone-600 font-normal">From: {defaults.observation_note}</div>}
          </div>
        )}
        {!fromScouting && (
          <>
            <Field label="Ranch / Vineyard">
              <select data-testid="task-ranch-select" value={t.ranch_number || ""} onChange={(e) => pickVineyard(e.target.value)} className="w-full h-12 px-3 border border-stone-300">
                <option value="">— Select Ranch (optional) —</option>
                {vineyards.map((r) => <option key={r.ranch_number} value={r.ranch_number}>{r.ranch_number} · {r.name}</option>)}
              </select>
            </Field>
            {t.ranch_number && blocksFor(t.ranch_number).length > 0 && (
              <Field label="Blocks (optional)">
                <div className="flex flex-wrap gap-2" data-testid="task-block-chips">
                  {blocksFor(t.ranch_number).map((b) => {
                    const sel = t.block_ids.includes(b.id);
                    return (
                      <button key={b.id} type="button" data-testid={`task-block-${b.id}`} onClick={() => toggleBlock(b)}
                        className={`h-11 px-4 text-sm font-semibold border ${sel ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300"}`}>
                        {b.block || b.name}{b.acres ? ` · ${b.acres} ac` : ""}
                      </button>
                    );
                  })}
                </div>
                {t.acres != null && <div className="text-xs text-stone-500 mt-1 font-semibold">{t.acres} acres selected</div>}
              </Field>
            )}
          </>
        )}
        <Field label="Task"><input data-testid="task-title-input" value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} className="w-full h-12 px-3 border border-stone-300" placeholder="e.g. Under-vine cultivation" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select data-testid="task-category-select" value={t.category} onChange={(e) => setT({ ...t, category: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
              {meta.categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Complete By"><input type="date" data-testid="task-due-input" value={t.due_date || ""} onChange={(e) => setT({ ...t, due_date: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
        </div>
        <Field label="Priority"><Chips options={["urgent", "high", "normal", "low"]} value={t.priority} onChange={(v) => setT({ ...t, priority: v || "normal" })} testId="task-priority-chips" /></Field>
        <Field label="Instructions"><textarea data-testid="task-instructions-input" value={t.instructions || ""} onChange={(e) => setT({ ...t, instructions: e.target.value })} rows={2} className="w-full border border-stone-300 p-3 text-sm" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Supervisor (optional)">
            <select value={t.supervisor_id || ""} onChange={(e) => setT({ ...t, supervisor_id: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
              <option value="">—</option>
              {employees.filter((e) => ["Supervisor", "Foreman", "Admin"].includes(e.category)).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
          <Field label="Crew (optional)">
            <select value={t.crew_id || ""} onChange={(e) => setT({ ...t, crew_id: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
              <option value="">—</option>
              {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Equipment (optional)">
          <select multiple value={t.equipment_ids} onChange={(e) => setT({ ...t, equipment_ids: [...e.target.selectedOptions].map((o) => o.value) })} className="w-full px-3 py-2 border border-stone-300 h-24">
            {equipment.map((e) => <option key={e.id} value={e.id}>{e.code ? `${e.code} · ` : ""}{e.name}</option>)}
          </select>
        </Field>
        <button data-testid="task-save-btn" onClick={save} disabled={saving || !t.title.trim()}
          className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40">
          {saving ? "Sending…" : "Send To Work List"}
        </button>
      </div>
    </div>
  );
}
